import { IIssueApi } from "@schemas/issueSchema";
import { AgCharts } from "ag-charts-react";
import { BreeamDataEnum, OtherAttributeEnum } from "./BreeamDashboard";

type BreeamBarChartProps = {
  items: IIssueApi[];
  dataFields: string[];
  issueBoard: any;
};

type PreanalyseSeries = {
  key: string;
  color: string;
  name: string;
  aliases: string[];
};

const possiblePointTitles = [
  "Kriteriet er utelatt",
  "Oppnår ikke poeng for kriteriet",
  "OppnÃƒÂ¥r ikke poeng for kriteriet",
  "Usikker- avklaring",
  "Antas for krevende å oppnå",
  "Antas for krevende ÃƒÂ¥ oppnÃƒÂ¥",
];

const preanalyseSeries: PreanalyseSeries[] = [
  {
    key: "notAchievable",
    color: "lightgray",
    name: "Antas ikke oppnåelig",
    aliases: ["Antas ikke oppnåelig", "Antas ikke oppnÃƒÂ¥elig", "Antas ikke oppnåelig"],
  },
  { key: "veryHard", color: "red", name: "Krever mye", aliases: ["Krever mye"] },
  { key: "hard", color: "orange", name: "Krever mer", aliases: ["Krever mer"] },
  { key: "some", color: "#FFEA00", name: "Krever noe", aliases: ["Krever noe"] },
  {
    key: "achievable",
    color: "green",
    name: "Antas oppnås",
    aliases: ["Antas oppnås", "Antas oppnÃƒÂ¥s", "Antas oppnås"],
  },
];

const normalizeText = (value: unknown) =>
  String(value ?? "")
    .normalize("NFKD")
    .replace(/\s+/g, "")
    .toLowerCase();

const sumIssuePoints = (items: IIssueApi[], sumKey: OtherAttributeEnum) =>
  items.reduce((total, item) => total + Number(item.otherAttributesData?.[sumKey] ?? 0), 0);

const MIN_LABEL_SHARE_OF_MAX = 0.02;

const shouldShowLabel = (value: number, maxVisibleValue: number) =>
  value > 0 && (maxVisibleValue <= 0 || value / maxVisibleValue >= MIN_LABEL_SHARE_OF_MAX);

const barLabel = {
  enabled: true,
  color: "#414141",
  fontSize: 12,
  fontWeight: 600,
};

const chartAxes = [
  {
    type: "number",
    position: "bottom",
    min: 0,
    title: { text: "Antall poeng" },
    gridLine: { enabled: false },
    line: { enabled: true, stroke: "#e3e3e3", width: 2 },
  },
  {
    type: "category",
    position: "left",
    label: {
      fontSize: 14,
      fontFamily: "helvetica, arial, Verdana, sans-serif",
    },
    line: { enabled: true, stroke: "#e3e3e3", width: 2 },
  },
];

const BreeamBarChart = ({ items, dataFields }: BreeamBarChartProps) => {
  const data = calculateTotals(items, dataFields);
  const maxPoints = data.reduce((max, row) => Math.max(max, Number(row.points) || 0), 0);

  const options: any = {
    height: Math.max(360, dataFields.length * 34 + 150),
    data,
    title: {
      text: "Status for BREEAM kriterier",
      color: "#464646",
      fontSize: 18,
    },
    series: [
      {
        type: "bar",
        direction: "horizontal",
        xKey: "bucket",
        yKey: "points",
        yName: "Antall poeng",
        fill: "#f59426",
        strokeWidth: 0,
        cornerRadius: 4,
        label: {
          ...barLabel,
          formatter: ({ value }) => (shouldShowLabel(Number(value), maxPoints) ? String(Number(value)) : ""),
        },
        itemStyler: ({ datum, fill }) => ({
          fill: datum.color ?? fill,
        }),
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.bucket,
            content: `${datum.points} poeng`,
          }),
        },
      },
    ],
    axes: chartAxes,
    legend: { enabled: false },
  };

  return <AgCharts options={options} />;
};

const calculateTotals = (items: IIssueApi[], dataFields: string[]) => {
  try {
    return dataFields.map((field) => {
      const bucketItems = items.filter((item) => item.bucket === field);
      const sumKey =
        field === BreeamDataEnum.BucketOptionOmitted ? OtherAttributeEnum.MaxPoints : OtherAttributeEnum.PlannedPoints;

      return {
        bucket: field,
        points: sumIssuePoints(bucketItems, sumKey),
        color: possiblePointTitles.some((title) => normalizeText(title) === normalizeText(field))
          ? "#a7a7a7"
          : "#f59426",
      };
    });
  } catch {
    return [];
  }
};

export const BreeamStackedBarChart = ({ items, dataFields, issueBoard }: BreeamBarChartProps) => {
  const data = calculateStackedTotals(items, dataFields, issueBoard);
  const maxStackTotal = data.reduce(
    (max, row) =>
      Math.max(
        max,
        preanalyseSeries.reduce((sum, series) => sum + Number((row as Record<string, unknown>)[series.key] ?? 0), 0),
      ),
    0,
  );

  const options: any = {
    height: Math.max(360, dataFields.length * 34 + 170),
    data,
    title: {
      text: "Status for BREEAM kriterier",
      color: "#464646",
      fontSize: 18,
    },
    series: preanalyseSeries.map((item) => ({
      type: "bar",
      direction: "horizontal",
      xKey: "typeOfIssue",
      yKey: item.key,
      yName: item.name,
      stacked: true,
      stackGroup: "preanalyse",
      fill: item.color,
      strokeWidth: 0,
      cornerRadius: 4,
      label: {
        ...barLabel,
        color: item.key === "achievable" ? "#ffffff" : barLabel.color,
        formatter: ({ value }) => (shouldShowLabel(Number(value), maxStackTotal) ? String(Number(value)) : ""),
      },
      tooltip: {
        renderer: ({ datum }) => ({
          title: item.name,
          content: `${datum.typeOfIssue}: ${datum[item.key] ?? 0}`,
        }),
      },
    })),
    axes: chartAxes,
    legend: {
      enabled: true,
      item: {
        marker: { shape: "square" },
        label: { fontSize: 15, color: "#636363" },
      },
    },
  };

  return <AgCharts options={options} />;
};

const findPreanalyseSeries = (value: unknown) =>
  preanalyseSeries.find((series) => series.aliases.some((alias) => normalizeText(alias) === normalizeText(value)));

const calculateStackedTotals = (items: IIssueApi[], dataFields: string[], issueBoard: any) => {
  try {
    const hasCustomPreanalyseOptions = Boolean(issueBoard.otherAttributesConfig?.B?.dropdownOptions?.length);

    return dataFields.map((field) =>
      items
        .filter((item) => item.typeOfIssue === field)
        .reduce(
          (row, item) => {
            const series = findPreanalyseSeries(
              hasCustomPreanalyseOptions ? item.otherAttributesData?.[OtherAttributeEnum.DifficultyLevel] : item.bucket,
            );
            if (!series) {
              return row;
            }

            const sumKey =
              series.key === "notAchievable" ? OtherAttributeEnum.MaxPoints : OtherAttributeEnum.PlannedPoints;
            row[series.key] = Number(row[series.key] ?? 0) + Number(item.otherAttributesData?.[sumKey] ?? 0);
            return row;
          },
          preanalyseSeries.reduce(
            (row, series) => ({
              ...row,
              [series.key]: 0,
            }),
            { typeOfIssue: field },
          ),
        ),
    );
  } catch {
    return [];
  }
};

export default BreeamBarChart;
